import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import Notification from '../../utils/notification';
import styles from './CheckInSection.module.css';

const CheckInSection = ({ userProfile, onCheckInSuccess }) => {
  const [checkInData, setCheckInData] = useState({
    hasCheckedToday: false,
    streak: 0,
    monthlyCheckIns: [], // 本月签到记录
    currentDate: new Date()
  });
  const [loading, setLoading] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [rewardInfo, setRewardInfo] = useState(null);

  useEffect(() => {
    // 获取签到状态
    const today = new Date().toDateString();
    const lastCheckIn = userProfile.lastCheckIn ? new Date(userProfile.lastCheckIn).toDateString() : null;
    const hasCheckedToday = lastCheckIn === today;

    // 生成本月签到记录
    const monthlyCheckIns = generateMonthlyCheckIns(userProfile.checkInStreak || 0);

    setCheckInData({
      hasCheckedToday,
      streak: userProfile.checkInStreak || 0,
      monthlyCheckIns,
      currentDate: new Date()
    });
  }, [userProfile]);

  const generateMonthlyCheckIns = (streak) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const checkedDates = [];

    // 模拟过去几天的签到记录
    for (let i = 0; i < Math.min(streak, 30); i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      if (date.getMonth() === currentMonth) {
        checkedDates.push(date.getDate());
      }
    }

    return checkedDates;
  };

  const handleCheckIn = async () => {
    if (checkInData.hasCheckedToday || loading) return;

    setLoading(true);
    try {
      await userAPI.checkin();

      // 简化奖励信息，固定显示2金币
      setRewardInfo({ coins: 2 });
      setShowReward(true);

      setCheckInData(prev => ({
        ...prev,
        hasCheckedToday: true,
        streak: prev.streak + 1,
        monthlyCheckIns: [...prev.monthlyCheckIns, new Date().getDate()]
      }));

      if (onCheckInSuccess) {
        onCheckInSuccess({ coinBalance: 2 });
      }

      // 3秒后自动关闭奖励弹窗
      setTimeout(() => {
        setShowReward(false);
      }, 3000);

    } catch (error) {
      console.error('Check-in failed:', error);
      Notification.error('签到失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 生成日历数据
  const generateCalendarDays = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    // 获取本月第一天和最后一天
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // 获取第一天是星期几（0=周日）
    const startingDayOfWeek = firstDay.getDay();

    // 生成日历数组
    const days = [];

    // 添加上个月的尾部日期
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(firstDay);
      date.setDate(date.getDate() - (i + 1));
      days.push({
        date: date.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isCheckedIn: false
      });
    }

    // 添加本月的所有日期
    for (let date = 1; date <= lastDay.getDate(); date++) {
      const isToday = date === today.getDate() &&
                     month === today.getMonth() &&
                     year === today.getFullYear();
      const isCheckedIn = checkInData.monthlyCheckIns.includes(date);

      days.push({
        date,
        isCurrentMonth: true,
        isToday,
        isCheckedIn
      });
    }

    // 添加下个月的开头日期，填满6周
    const remainingCells = 42 - days.length; // 6周 × 7天
    for (let date = 1; date <= remainingCells; date++) {
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isCheckedIn: false
      });
    }

    return days;
  };

  return (
    <div className={styles.checkinSection}>
      <div className={styles.checkinHeader}>
        <h2>每日签到</h2>
        <p>连续签到获得更多奖励，坚持签到享受更多福利</p>
      </div>

      <div className={styles.checkinContent}>
        <div className={styles.checkinStatus}>
          <div className={styles.streakInfo}>
            <div className={styles.streakNumber}>
              {/* <span className={styles.number}>{checkInData.streak}</span>
              <span className={styles.label}>连续签到天数</span> */}
            </div>
            <div className={styles.checkinButtonSection}>
              <button
                className={`${styles.checkinBtn} ${checkInData.hasCheckedToday ? styles.checked : ''}`}
                onClick={handleCheckIn}
                disabled={checkInData.hasCheckedToday || loading}
              >
                {loading ? (
                  <span>签到中...</span>
                ) : checkInData.hasCheckedToday ? (
                  <span>✅ 今日已签到</span>
                ) : (
                  <span>📅 立即签到 (+2金币)</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 真正的月历视图 */}
        <div className={styles.calendarContainer}>
          <div className={styles.calendarHeader}>
            <h3>{checkInData.currentDate.getFullYear()}年{checkInData.currentDate.getMonth() + 1}月</h3>
            <p>签到日历</p>
          </div>

          <div className={styles.calendar}>
            <div className={styles.weekHeader}>
              {['日', '一', '二', '三', '四', '五', '六'].map(day => (
                <div key={day} className={styles.weekDay}>{day}</div>
              ))}
            </div>

            <div className={styles.calendarGrid}>
              {generateCalendarDays().map((day, index) => (
                <div
                  key={index}
                  className={`
                    ${styles.calendarDay}
                    ${day.isCurrentMonth ? styles.currentMonth : styles.otherMonth}
                    ${day.isToday ? styles.today : ''}
                    ${day.isCheckedIn ? styles.checkedIn : ''}
                  `}
                >
                  <span className={styles.dayNumber}>{day.date}</span>
                  {day.isCheckedIn && day.isCurrentMonth && (
                    <div className={styles.checkMark}>✓</div>
                  )}
                  {day.isToday && (
                    <div className={styles.todayIndicator}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.checkinStats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>📊</div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{userProfile.totalCheckIns || 0}</span>
              <span className={styles.statLabel}>累计签到</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>💎</div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{userProfile.coinBalance || 0}</span>
              <span className={styles.statLabel}>当前金币</span>
            </div>
          </div>
          {/* <div className={styles.statCard}>
            <div className={styles.statIcon}>🏆</div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{Math.floor((userProfile.totalCheckIns || 0) / 10)}</span>
              <span className={styles.statLabel}>完整周期</span>
            </div>
          </div> */}
        </div>
      </div>

      {/* 签到奖励弹窗 */}
      {showReward && rewardInfo && (
        <div className={styles.rewardModal}>
          <div className={styles.rewardContent}>
            <div className={styles.rewardIcon}>🎉</div>
            <h3>签到成功！</h3>
            <div className={styles.rewardDetails}>
              <div className={styles.rewardItem}>
                <span>获得金币：</span>
                <span className={styles.rewardValue}>+2</span>
              </div>
            </div>
            <button
              className={styles.closeRewardBtn}
              onClick={() => setShowReward(false)}
            >
              确定
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInSection;